import React, { ReactNode, useState } from 'react';
import {
    PlayCircle, Database, Settings, ChevronDown, ChevronUp,
    Mail, Cloud, Zap, CheckCircle, AlertTriangle, FileText,
    Loader
} from 'lucide-react';
import DetailedNodeLogModal from './DetailedNodeLogModal';

// in WorkflowTimeline.tsx
export interface TimelineEntry {
    id: string;
    name: string;
    type: 'start' | 'load' | 'agent' | 'condition' | 'branch' | 'api' | 'email' | 'end';
    status: 'Success' | 'Failed' | 'Running';
    time: string;
    input: string;
    output: string;
    nested?: TimelineEntry[];
}


const iconMap = {
    start: PlayCircle,
    load: Database,
    agent: Settings,
    condition: FileText,
    branch: Zap,
    api: Cloud,
    email: Mail,
    end: CheckCircle,
};

  interface LogEvent {
  type: 'info' | 'json' | 'table';
  icon: ReactNode;
  label: string;
  content: string | Record<string, any> | string[][];
}
const statusColor = {
    Success: 'text-green-400',
    Failed: 'text-red-400',
    Running: 'text-yellow-400',
};
const exampleEvents: LogEvent[] = [
  {
    type: 'info',
    icon: <Loader className="w-4 h-4 text-indigo-400" />,
    label: 'Node Execution Started',
    content: 'Execution began at 10:03:20 AM for node "Risk Analyzer Agent".',
  },
  {
    type: 'json',
    icon: <FileText className="w-4 h-4 text-blue-400" />,
    label: 'Received Input Payload',
    content: {
      userId: 'u12345',
      loanAmount: 50000,
      category: 'Small Business',
      documents: ['doc-2233.pdf', 'doc-2234.pdf'],
    },
  },
  {
    type: 'info',
    icon: <Database className="w-4 h-4 text-orange-400" />,
    label: 'Vector DB: Qdrant Query Executed',
    content: 'Searched Qdrant collection `user-doc-embeddings` with cosine similarity, found 5 relevant matches.',
  },
  {
    type: 'table',
    icon: <CheckCircle className="w-4 h-4 text-green-400" />,
    label: 'Semantic Vector Matches (Top 3)',
    content: [
      ['Document ID', 'Similarity Score'],
      ['doc-9981', '0.94'],
      ['doc-2254', '0.88'],
      ['doc-7762', '0.85'],
    ],
  },
  {
    type: 'json',
    icon: <Zap className="w-4 h-4 text-yellow-400" />,
    label: 'LLM Request Sent to GPT-4o',
    content: {
      prompt: 'Based on user context and document matches, assess fraud likelihood and suggest action.',
      contextChunks: 3,
      model: 'gpt-4o-mini',
    },
  },
  {
    type: 'json',
    icon: <CheckCircle className="w-4 h-4 text-green-300" />,
    label: 'LLM Response Received',
    content: {
      decision: 'Flagged for Review',
      confidence: '87%',
      notes: 'Multiple document anomalies and unusual claim pattern.',
    },
  },
  {
    type: 'info',
    icon: <Database className="w-4 h-4 text-teal-400" />,
    label: 'Database Write: Risk Events Logged',
    content: 'Saved risk event summary and LLM decision to PostgreSQL → table `risk_ledger` with event_id `ev_10201`.',
  },
  {
    type: 'info',
    icon: <CheckCircle className="w-4 h-4 text-green-400" />,
    label: 'Final Output Sent to Workflow Executor',
    content: 'Node output delivered successfully: action = escalate, route = compliance-recheck.',
  },
];

const WorkflowTimeline: React.FC<{ entries: TimelineEntry[] }> = ({ entries }) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const toggleExpand = (id: string) => {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const [selectedNodeLog, setSelectedNodeLog] = useState<TimelineEntry | null>(null);
    const [showFullLog, setShowFullLog] = useState(false);


    return (
        <div className="space-y-4 px-4 py-2 max-h-[65vh] overflow-y-auto">
            {entries.map((entry) => {
                const Icon = iconMap[entry.type] || FileText;
                return (
                    <div key={entry.id} className="flex items-start gap-3 border-l-2 border-neutral-700 pl-6 relative">
                        <span className="absolute left-0 top-2 -ml-[9px] bg-neutral-900 rounded-full p-1">
                            <Icon className={`w-4 h-4 ${statusColor[entry.status]}`} />
                        </span>
                        <div className="w-full">
                            <div className="flex justify-between items-center">
                                <div className="text-white font-semibold text-sm">{entry.name}</div>
                                <div className={`text-xs ${statusColor[entry.status]}`}>{entry.status}</div>
                            </div>
                            <div className="text-xs text-gray-400">{entry.time}</div>

                            <div className="mt-2 text-xs bg-neutral-800 rounded p-2 border border-neutral-700">
                                <div className="text-gray-400">Input:</div>
                                <div className="font-mono truncate text-gray-300">{entry.input}</div>
                                <div className="text-gray-400 mt-1">Output:</div>
                                <div className="font-mono truncate text-gray-300">{entry.output}</div>

                                <div className="mt-2 text-right">
                                    <button
                                        className="text-indigo-400 hover:underline text-xs"
                                        onClick={() => {
                                            setSelectedNodeLog(entry);
                                            setShowFullLog(true);
                                        }}
                                    >
                                        View Full
                                    </button>
                                </div>
                            </div>

                            {entry.nested && entry.nested.length > 0 && (
                                <div className="mt-2">
                                    <button
                                        onClick={() => toggleExpand(entry.id)}
                                        className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                                    >
                                        {expanded[entry.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        Nested Logs
                                    </button>
                                    {expanded[entry.id] && (
                                        <div className="ml-4 mt-2">
                                            <WorkflowTimeline entries={entry.nested} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {showFullLog && selectedNodeLog && (
                <DetailedNodeLogModal
                    nodeName={selectedNodeLog.name}
                    nodeTime={selectedNodeLog.time}
                    status={selectedNodeLog.status}
                    onClose={() => setShowFullLog(false)}
                    events={exampleEvents}
                />
            )}

        </div>
    );
};

export default WorkflowTimeline;
